import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  LogIn,
  LogOut,
  Pencil,
  Trash2,
  Download,
  History,
  ClipboardList,
  Settings2,
  Plus,
  Power,
  Tag,
  PieChart,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Input, Button, Badge, Select, Modal } from '../../Components/ui';
import { useToast, useConfirm } from '../../Components/Feedback';
import { AREAS, AREA_LABEL } from '../../utils/areas';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

// Mesmo padrão de DailyReport.jsx (getTodayLocal) — usado só pros filtros de
// PERÍODO (histórico, visão agregada), que são consulta, não ação. Bater
// ponto em si nunca usa isso: o backend trava no dia de hoje em Brasília e
// ignora qualquer data que o cliente mande (ver hojeBrasilia em
// backend/pontos.js) — o educador nem tem mais campo de data pra isso.
const hojeLocal = () => {
  const agora = new Date();
  const offset = agora.getTimezoneOffset() * 60000;
  return new Date(agora - offset).toISOString().split('T')[0];
};

const diasAtras = (n) => {
  const d = new Date(hojeLocal());
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

// Primeiro dia do mês ATUAL — data de início padrão de "Meu Histórico", pra
// abrir sempre mostrando "o mês corrente até hoje", não uma janela rolante
// de 30 dias (que cruzava mês e não batia com o que o educador esperava ver).
const primeiroDiaMesAtual = () => `${hojeLocal().slice(0, 7)}-01`;

// O backend devolve "YYYY-MM-DDTHH:MM:SS" já em horário de Brasília (ver
// DATE_FORMAT em backend/pontos.js) — extrai o texto direto, sem passar por
// `new Date(...)`, que reaplicaria o fuso do navegador por cima de um valor
// que já está certo e dobraria/zeraria o deslocamento.
const formatarHora = (iso) => (iso ? iso.slice(11, 19) : null);

const formatarData = (data) => {
  if (!data) return '—';
  const str =
    typeof data === 'string'
      ? data.slice(0, 10)
      : new Date(data).toISOString().slice(0, 10);
  const partes = str.split('-');
  if (partes.length < 3) return str;
  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
};

const duracao = (entrada, saida) => {
  if (!entrada || !saida) return null;
  const ms = new Date(saida) - new Date(entrada);
  if (ms <= 0) return null;
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
};

const segundosDeDuracao = (entrada, saida) => {
  if (!entrada || !saida) return 0;
  const ms = new Date(saida) - new Date(entrada);
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.floor(ms / 1000);
};

const formatarSegundosHMS = (totalSeg) => {
  const seg = Math.max(0, Math.floor(totalSeg));
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};

const calcularResumoCategorias = (registros = []) => {
  const mapa = new Map();
  let totalSegundos = 0;

  for (const r of registros) {
    const nomeCategoria = r.categoria_nome || (r.id_atividade ? 'Aula' : 'Geral');
    const seg = segundosDeDuracao(r.hora_entrada, r.hora_saida);
    totalSegundos += seg;
    const atual = mapa.get(nomeCategoria) || { categoria: nomeCategoria, segundos: 0, qtd: 0 };
    atual.segundos += seg;
    atual.qtd += 1;
    mapa.set(nomeCategoria, atual);
  }

  const itens = Array.from(mapa.values())
    .sort((a, b) => b.segundos - a.segundos || a.categoria.localeCompare(b.categoria))
    .map((item) => ({
      ...item,
      percentual: totalSegundos > 0 ? Math.round((item.segundos / totalSegundos) * 100) : 0,
      tempoFormatado: formatarSegundosHMS(item.segundos),
    }));

  return {
    itens,
    totalSegundos,
    totalFormatado: formatarSegundosHMS(totalSegundos),
  };
};

function ResumoCategorias({ registros = [], className = '' }) {
  const resumo = useMemo(() => calcularResumoCategorias(registros), [registros]);

  if (!registros.length || resumo.itens.length === 0) return null;

  return (
    <div
      className={`rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3.5 ${className}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <PieChart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Tempo por Categoria
          </h3>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Total computado:{' '}
          <strong className="text-gray-900 dark:text-gray-100 font-semibold">
            {resumo.totalFormatado}
          </strong>
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
        {resumo.itens.map((item) => (
          <div
            key={item.categoria}
            className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-2.5 flex flex-col justify-between gap-1.5"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                {item.categoria}
              </span>
              <Badge tone="neutral">
                {item.qtd} {item.qtd === 1 ? 'reg.' : 'regs.'}
              </Badge>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                {item.tempoFormatado}
              </span>
              <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
                {item.percentual}%
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-600 dark:bg-blue-500 transition-all"
                style={{ width: `${Math.min(100, Math.max(0, item.percentual))}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tela de ponto do educador: registra entrada/saída por AULA (turma + hoje),
// nunca outro dia — cada turma só aparece pra quem dá aula nela, no dia da
// semana de hoje (ver backend/pontos.js). O educador comum não corrige nem o
// próprio ponto: só um coordenador (geral, ou de área — escopo resolvido no
// backend) ou master corrigem.
function Pontos() {
  const { institutionId } = useInstitution();
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const ehProfessor = user?.perfil === 'professor';
  const ehGestor = ['master', 'coordenador'].includes(user?.perfil);

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );
  const api = useApi();

  // --- Modal de correção de horário e atividade (compartilhado com quem tem permissão
  // de editar — master ou coordenador) ---
  const [pontoEmEdicao, setPontoEmEdicao] = useState(null);
  const [dataEdicao, setDataEdicao] = useState('');
  const [horaEntradaEdicao, setHoraEntradaEdicao] = useState('');
  const [horaSaidaEdicao, setHoraSaidaEdicao] = useState('');
  const [atividadeEdicao, setAtividadeEdicao] = useState('');
  const [categoriaEdicao, setCategoriaEdicao] = useState('');

  const { data: turmasDisponiveis = [] } = useQuery({
    queryKey: ['atividades', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/atividades');
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && ehGestor,
  });

  const { data: tiposInternos = [] } = useQuery({
    queryKey: ['tipos-ponto-interno', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/tipos-ponto-interno');
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && ehGestor,
  });

  const { data: categoriasDisponiveis = [] } = useQuery({
    queryKey: ['categorias-ponto', institutionId],
    queryFn: async () => {
      const res = await api.get('/api/categorias-ponto');
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && ehGestor,
  });

  const opcoesAtividades = useMemo(() => {
    if (!pontoEmEdicao) return [];

    const opcoes = [];
    const idProf = pontoEmEdicao.id_professor ? Number(pontoEmEdicao.id_professor) : null;
    const nomeProf = pontoEmEdicao.nome_professor;

    // Filtra apenas as turmas/atividades do educador da linha que está sendo editada
    const turmasDoEducador = turmasDisponiveis.filter((t) => {
      if (idProf) {
        const ehPrincipal = Number(t.idprofessor) === idProf;
        const ehCoProf =
          Array.isArray(t.professores_adicionais) &&
          t.professores_adicionais.some((p) => Number(p.id) === idProf);
        return ehPrincipal || ehCoProf;
      }
      if (nomeProf) {
        return t.nome_professor === nomeProf;
      }
      return true;
    });

    turmasDoEducador.forEach((t) => {
      const detalhes = [t.dia_semana, t.horario].filter(Boolean).join(' ');
      opcoes.push({
        value: `turma-${t.id}`,
        label: detalhes ? `${t.nome} (${detalhes})` : t.nome,
      });
    });

    // Filtra atividades internas pelas áreas onde o educador leciona
    const areasDoEducador = new Set(turmasDoEducador.map((t) => t.area).filter(Boolean));
    const tiposDoEducador = tiposInternos.filter((ti) => {
      if (areasDoEducador.size > 0) {
        return areasDoEducador.has(ti.area);
      }
      return true;
    });

    tiposDoEducador.forEach((ti) => {
      opcoes.push({
        value: `tipo-${ti.id}`,
        label: `[Interno] ${ti.nome}`,
      });
    });

    // Se o ponto em edição possui uma atividade que não conste nas opções filtradas,
    // inclui ela para manter a opção atual selecionada e visível
    if (
      pontoEmEdicao?.nome_turma &&
      atividadeEdicao &&
      !opcoes.some((o) => String(o.value) === String(atividadeEdicao))
    ) {
      opcoes.unshift({
        value: atividadeEdicao,
        label: pontoEmEdicao.nome_turma,
      });
    }

    return opcoes;
  }, [turmasDisponiveis, tiposInternos, pontoEmEdicao, atividadeEdicao]);

  const abrirEdicao = (ponto) => {
    setPontoEmEdicao(ponto);
    const dataInicial = ponto.data
      ? typeof ponto.data === 'string'
        ? ponto.data.slice(0, 10)
        : new Date(ponto.data).toISOString().slice(0, 10)
      : '';
    setDataEdicao(dataInicial);
    setHoraEntradaEdicao(ponto.hora_entrada ? formatarHora(ponto.hora_entrada) : '');
    setHoraSaidaEdicao(ponto.hora_saida ? formatarHora(ponto.hora_saida) : '');
    setCategoriaEdicao(ponto.id_categoria ? String(ponto.id_categoria) : '');

    if (ponto.id_atividade) {
      setAtividadeEdicao(`turma-${ponto.id_atividade}`);
    } else if (ponto.id_tipo_interno) {
      setAtividadeEdicao(`tipo-${ponto.id_tipo_interno}`);
    } else {
      const idProf = ponto.id_professor ? Number(ponto.id_professor) : null;
      const matchTurma = turmasDisponiveis.find((t) => {
        const mesmoNome = t.nome === ponto.nome_turma;
        if (!mesmoNome) return false;
        if (!idProf) return true;
        return (
          Number(t.idprofessor) === idProf ||
          t.professores_adicionais?.some((p) => Number(p.id) === idProf)
        );
      });
      if (matchTurma) {
        setAtividadeEdicao(`turma-${matchTurma.id}`);
      } else {
        const matchTipo = tiposInternos.find((ti) => ti.nome === ponto.nome_turma);
        if (matchTipo) {
          setAtividadeEdicao(`tipo-${matchTipo.id}`);
        } else {
          setAtividadeEdicao('');
        }
      }
    }
  };

  const aoAlterarAtividadeEdicao = (valor) => {
    setAtividadeEdicao(valor);
    if (valor?.startsWith('tipo-')) {
      const idTipo = Number(valor.replace('tipo-', ''));
      const tipoObj = tiposInternos.find((ti) => Number(ti.id) === idTipo);
      if (tipoObj?.id_categoria) {
        setCategoriaEdicao(String(tipoObj.id_categoria));
      }
    } else if (valor?.startsWith('turma-')) {
      const catAula = categoriasDisponiveis.find(
        (c) => c.nome?.toLowerCase() === 'aula' && c.ativo,
      );
      if (catAula) {
        setCategoriaEdicao(String(catAula.id));
      }
    }
  };

  const salvarEdicao = async () => {
    try {
      const dataFinal =
        dataEdicao || (pontoEmEdicao.data ? String(pontoEmEdicao.data).slice(0, 10) : '');
      if (!dataFinal) {
        toast.warning('A data é obrigatória.');
        return;
      }
      const payload = {
        data: dataFinal,
        hora_entrada: horaEntradaEdicao ? `${dataFinal} ${horaEntradaEdicao}` : null,
        hora_saida: horaSaidaEdicao ? `${dataFinal} ${horaSaidaEdicao}` : null,
      };

      if (atividadeEdicao?.startsWith('turma-')) {
        payload.id_atividade = Number(atividadeEdicao.replace('turma-', ''));
        payload.id_tipo_interno = null;
      } else if (atividadeEdicao?.startsWith('tipo-')) {
        payload.id_tipo_interno = Number(atividadeEdicao.replace('tipo-', ''));
        payload.id_atividade = null;
      }

      if (categoriaEdicao !== '') {
        payload.id_categoria = Number(categoriaEdicao);
      }

      const res = await api.put(`/api/pontos/${pontoEmEdicao.id}`, payload);
      toast.success(res?.message || 'Registro corrigido.');
      setPontoEmEdicao(null);
      queryClient.invalidateQueries({ queryKey: ['pontos-turmas'] });
      queryClient.invalidateQueries({ queryKey: ['pontos-agregado'] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const excluirPonto = async (ponto) => {
    if (
      !(await confirm('Apagar esse registro de ponto?', {
        title: 'Apagar ponto',
        confirmText: 'Apagar',
      }))
    )
      return;
    try {
      const res = await api.del(`/api/pontos/${ponto.id}`);
      toast.success(res?.message || 'Registro apagado.');
      queryClient.invalidateQueries({ queryKey: ['pontos-turmas'] });
      queryClient.invalidateQueries({ queryKey: ['pontos-agregado'] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  const opcoesCategoriasModal = useMemo(
    () => [
      { value: '', label: 'Padrão da atividade' },
      ...categoriasDisponiveis
        .filter((c) => c.ativo || String(c.id) === String(categoriaEdicao))
        .map((c) => ({ value: String(c.id), label: c.nome })),
    ],
    [categoriasDisponiveis, categoriaEdicao],
  );

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-6xl mx-auto space-y-6">
        {ehProfessor && (
          <SecaoEducador
            authHeaders={authHeaders}
            institutionId={institutionId}
            toast={toast}
            queryClient={queryClient}
          />
        )}
        {ehGestor && (
          <SecaoGestor
            authHeaders={authHeaders}
            institutionId={institutionId}
            abrirEdicao={abrirEdicao}
            excluirPonto={excluirPonto}
            user={user}
            toast={toast}
            confirm={confirm}
            queryClient={queryClient}
            categorias={categoriasDisponiveis}
          />
        )}
      </main>

      <Modal
        isOpen={!!pontoEmEdicao}
        onClose={() => setPontoEmEdicao(null)}
        title="Corrigir registro"
        maxWidth="sm"
      >
        {pontoEmEdicao && (
          <div className="space-y-4">
            {pontoEmEdicao.nome_professor && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Educador:{' '}
                <strong className="text-gray-700 dark:text-gray-300">
                  {pontoEmEdicao.nome_professor}
                </strong>
              </p>
            )}
            <Input
              type="date"
              name="data"
              label="Data"
              value={dataEdicao}
              onChange={(e) => setDataEdicao(e.target.value)}
              required
            />
            {opcoesAtividades.length > 0 && (
              <Select
                name="atividade"
                label="Atividade / Turma"
                value={atividadeEdicao}
                onChange={(e) => aoAlterarAtividadeEdicao(e.target.value)}
                options={opcoesAtividades}
              />
            )}
            {categoriasDisponiveis.length > 0 && (
              <Select
                name="categoria"
                label="Categoria"
                value={categoriaEdicao}
                onChange={(e) => setCategoriaEdicao(e.target.value)}
                options={opcoesCategoriasModal}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="time"
                step="1"
                label="Entrada"
                value={horaEntradaEdicao}
                onChange={(e) => setHoraEntradaEdicao(e.target.value)}
              />
              <Input
                type="time"
                step="1"
                label="Saída"
                value={horaSaidaEdicao}
                onChange={(e) => setHoraSaidaEdicao(e.target.value)}
              />
            </div>
            <Button variant="primary" onClick={salvarEdicao} className="w-full">
              Salvar
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// --- Visão do educador: bater ponto nas turmas de HOJE (data travada, sem
// seletor) + histórico próprio, só consulta (sem corrigir/apagar — ver
// comentário no topo do arquivo). ---
function SecaoEducador({ authHeaders, institutionId, toast, queryClient }) {
  const { podeCriar, podeEditar } = usePermissao('/pontos');
  const [processandoId, setProcessandoId] = useState(null);

  const { data: resposta, isLoading } = useQuery({
    queryKey: ['pontos-turmas', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/pontos/turmas`, { headers: authHeaders });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar turmas');
      return json?.data ?? json;
    },
    enabled: !!institutionId,
  });
  const turmas = resposta?.turmas || [];
  const atividadesInternas = resposta?.atividades_internas || [];
  const data = resposta?.data;
  const diaSemana = resposta?.dia_semana;

  // Só dá pra ter UM ponto em aberto por vez (turma OU atividade interna) —
  // mesma regra do backend (ver buscarPontoAberto em backend/pontos.js).
  // Achando um aqui, desabilita "Bater Ponto" nas OUTRAS linhas em vez de
  // deixar o clique estourar o erro 409 — a linha que já está em andamento
  // nem passa por essa checagem (ela mostra "Registrar Saída", não "Bater
  // Ponto", então não teria como ela mesma se auto-bloquear aqui).
  const itemEmAndamento = [...turmas, ...atividadesInternas].find(
    (item) => item.hora_entrada && !item.hora_saida,
  );

  const [historicoInicio, setHistoricoInicio] = useState(primeiroDiaMesAtual);
  const [historicoFim, setHistoricoFim] = useState(hojeLocal);

  const { data: historico = [] } = useQuery({
    queryKey: ['pontos-historico', institutionId, historicoInicio, historicoFim],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/pontos/meu-historico?data_inicio=${historicoInicio}&data_fim=${historicoFim}`,
        { headers: authHeaders },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar histórico');
      return json?.data ?? (Array.isArray(json) ? json : []);
    },
    enabled: !!institutionId,
  });

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ['pontos-turmas'] });
    queryClient.invalidateQueries({ queryKey: ['pontos-historico'] });
  };

  const bater = async (turma) => {
    setProcessandoId(`turma-${turma.id_atividade}`);
    try {
      const res = await fetch(`${API_URL}/api/pontos/bater`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id_atividade: turma.id_atividade }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao bater ponto');
      toast.success(`Entrada registrada — ${turma.nome}.`);
      recarregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const registrarSaida = async (turma) => {
    setProcessandoId(`turma-${turma.id_atividade}`);
    try {
      const res = await fetch(`${API_URL}/api/pontos/saida`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id_atividade: turma.id_atividade }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao registrar saída');
      toast.success(`Saída registrada — ${turma.nome}.`);
      recarregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const baterInterno = async (atividade) => {
    setProcessandoId(`interno-${atividade.id_tipo_interno}`);
    try {
      const res = await fetch(`${API_URL}/api/pontos/bater-interno`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id_tipo_interno: atividade.id_tipo_interno }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao bater ponto');
      toast.success(`Entrada registrada — ${atividade.nome}.`);
      recarregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const registrarSaidaInterno = async (atividade) => {
    setProcessandoId(`interno-${atividade.id_tipo_interno}`);
    try {
      const res = await fetch(`${API_URL}/api/pontos/saida-interno`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id_tipo_interno: atividade.id_tipo_interno }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao registrar saída');
      toast.success(`Saída registrada — ${atividade.nome}.`);
      recarregar();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessandoId(null);
    }
  };

  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Registrar Atividade
              </h2>
              {data && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {diaSemana}-feira, {formatarData(data)}
                </p>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">Carregando...</p>
        ) : turmas.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
            Você não tem turma hoje.
          </p>
        ) : (
          <div className="space-y-2">
            {turmas.map((t) => {
              const concluido = t.hora_entrada && t.hora_saida;
              const emAndamento = t.hora_entrada && !t.hora_saida;
              return (
                <div
                  key={t.id_atividade}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {t.nome}
                      </p>
                      <Badge tone="neutral">{t.categoria_nome || 'Aula'}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t.horario} · {t.turno}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {concluido ? (
                      <Badge tone="success">
                        {formatarHora(t.hora_entrada)} – {formatarHora(t.hora_saida)}
                      </Badge>
                    ) : emAndamento ? (
                      <>
                        <Badge tone="warning">Desde {formatarHora(t.hora_entrada)}</Badge>
                        {podeEditar && (
                          <Button
                            variant="secondary"
                            icon={LogOut}
                            onClick={() => registrarSaida(t)}
                            disabled={processandoId === `turma-${t.id_atividade}`}
                          >
                            Registrar Saída
                          </Button>
                        )}
                      </>
                    ) : podeCriar ? (
                      <Button
                        variant="primary"
                        icon={LogIn}
                        onClick={() => bater(t)}
                        disabled={processandoId === `turma-${t.id_atividade}` || !!itemEmAndamento}
                        title={
                          itemEmAndamento
                            ? `Encerre o registro em "${itemEmAndamento.nome}" antes de bater outro.`
                            : undefined
                        }
                      >
                        Registrar Atividade
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {atividadesInternas.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Outras Atividades
            </h2>
          </div>
          <div className="space-y-2">
            {atividadesInternas.map((a) => {
              const concluido = a.hora_entrada && a.hora_saida;
              const emAndamento = a.hora_entrada && !a.hora_saida;
              return (
                <div
                  key={a.id_tipo_interno}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {a.nome}
                      </p>
                      {a.categoria_nome && <Badge tone="neutral">{a.categoria_nome}</Badge>}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {AREA_LABEL[a.area] || a.area}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {concluido ? (
                      <Badge tone="success">
                        {formatarHora(a.hora_entrada)} – {formatarHora(a.hora_saida)}
                      </Badge>
                    ) : emAndamento ? (
                      <>
                        <Badge tone="warning">Desde {formatarHora(a.hora_entrada)}</Badge>
                        {podeEditar && (
                          <Button
                            variant="secondary"
                            icon={LogOut}
                            onClick={() => registrarSaidaInterno(a)}
                            disabled={processandoId === `interno-${a.id_tipo_interno}`}
                          >
                            Registrar Saída
                          </Button>
                        )}
                      </>
                    ) : podeCriar ? (
                      <Button
                        variant="primary"
                        icon={LogIn}
                        onClick={() => baterInterno(a)}
                        disabled={
                          processandoId === `interno-${a.id_tipo_interno}` || !!itemEmAndamento
                        }
                        title={
                          itemEmAndamento
                            ? `Encerre o registro em "${itemEmAndamento.nome}" antes de bater outro.`
                            : undefined
                        }
                      >
                        Registrar Atividade
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Meu Histórico
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              size="sm"
              value={historicoInicio}
              onChange={(e) => setHistoricoInicio(e.target.value)}
            />
            <span className="text-xs text-gray-400">até</span>
            <Input
              type="date"
              size="sm"
              value={historicoFim}
              onChange={(e) => setHistoricoFim(e.target.value)}
            />
          </div>
        </div>
        {historico.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
            Nenhum registro nesse período.
          </p>
        ) : (
          <div className="space-y-4">
            <ResumoCategorias registros={historico} />
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
                    <th className="py-2 pr-3 font-medium">Data</th>
                    <th className="py-2 pr-3 font-medium">Turma</th>
                    <th className="py-2 pr-3 font-medium">Categoria</th>
                    <th className="py-2 pr-3 font-medium">Entrada</th>
                    <th className="py-2 pr-3 font-medium">Saída</th>
                    <th className="py-2 font-medium">Duração</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                  {historico.map((h) => (
                    <tr key={h.id}>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {formatarData(h.data)}
                      </td>
                      <td
                        className="py-2 pr-3 text-gray-700 dark:text-gray-300 truncate"
                        translate="no"
                      >
                        {h.nome_turma}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">
                        <Badge tone="neutral">{h.categoria_nome || 'Aula'}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {formatarHora(h.hora_entrada) || '—'}
                      </td>
                      <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                        {formatarHora(h.hora_saida) || '—'}
                      </td>
                      <td className="py-2 text-gray-500 dark:text-gray-400">
                        {duracao(h.hora_entrada, h.hora_saida) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

// --- Visão agregada: master vê todos os educadores; coordenador geral
// também vê todos; coordenador de área vê só a área dele — o backend já
// limita os dados e a permissão de editar/apagar nos três casos, então este
// componente não precisa saber qual escopo está ativo. ---
function SecaoGestor({
  authHeaders,
  institutionId,
  abrirEdicao,
  excluirPonto,
  user,
  toast,
  confirm,
  queryClient,
  categorias = [],
}) {
  const api = useApi();
  const { podeCriar, podeEditar, podeExcluir, podeExportar } = usePermissao('/pontos');
  const [inicio, setInicio] = useState(() => diasAtras(60));
  const [fim, setFim] = useState(hojeLocal);
  const [idProfessor, setIdProfessor] = useState('');

  const { data: educadores = [] } = useQuery({
    queryKey: ['pontos-educadores', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/pontos/educadores`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId,
  });

  const { data: pontos = [], isLoading } = useQuery({
    queryKey: ['pontos-agregado', institutionId, inicio, fim, idProfessor],
    queryFn: async () => {
      const params = new URLSearchParams({ data_inicio: inicio, data_fim: fim });
      if (idProfessor) params.set('id_professor', idProfessor);
      const res = await fetch(`${API_URL}/api/pontos?${params}`, { headers: authHeaders });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar pontos');
      return json?.data ?? (Array.isArray(json) ? json : []);
    },
    enabled: !!institutionId,
  });

  // Relatório em PDF (papel timbrado do Instituto) — sempre de UM educador
  // por vez (ver backend/relatorio-ponto-pdf.js: cabeçalho com nome/e-mail do
  // prestador, subtotal por dia e assinatura no final não fazem sentido
  // misturando gente). Por isso só habilita com um educador escolhido no
  // filtro — "Todos os educadores" continua útil só pra CONFERIR a tabela
  // na tela, não pra gerar o relatório.
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const gerarPDF = async () => {
    setGerandoPdf(true);
    try {
      const params = new URLSearchParams({
        data_inicio: inicio,
        data_fim: fim,
        id_professor: idProfessor,
      });
      const res = await fetch(`${API_URL}/api/pontos/relatorio-pdf?${params}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Erro ao gerar o relatório em PDF.');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RegistroAtividades_${inicio}_a_${fim}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGerandoPdf(false);
    }
  };

  // --- Categorias de Atividade (Aula, Planejamento, Estudo, Prática, ...) ---
  const [novoNomeCategoria, setNovoNomeCategoria] = useState('');
  const [salvandoCategoria, setSalvandoCategoria] = useState(false);

  const recarregarCategorias = () => {
    queryClient.invalidateQueries({ queryKey: ['categorias-ponto'] });
    queryClient.invalidateQueries({ queryKey: ['tipos-ponto-interno'] });
    queryClient.invalidateQueries({ queryKey: ['pontos-agregado'] });
  };

  const criarCategoria = async (e) => {
    e.preventDefault();
    if (!novoNomeCategoria.trim()) return;
    setSalvandoCategoria(true);
    try {
      const res = await fetch(`${API_URL}/api/categorias-ponto`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ nome: novoNomeCategoria.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao criar categoria');
      toast.success(json?.message || 'Categoria criada.');
      setNovoNomeCategoria('');
      recarregarCategorias();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoCategoria(false);
    }
  };

  const alternarAtivoCategoria = async (cat) => {
    try {
      const res = await fetch(`${API_URL}/api/categorias-ponto/${cat.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ ativo: cat.ativo ? 0 : 1 }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao atualizar categoria');
      recarregarCategorias();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const apagarCategoria = async (cat) => {
    if (
      !(await confirm(`Apagar a categoria "${cat.nome}"?`, {
        title: 'Apagar categoria',
        confirmText: 'Apagar',
      }))
    )
      return;
    try {
      const res = await fetch(`${API_URL}/api/categorias-ponto/${cat.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao apagar categoria');
      toast.success(json?.message || 'Categoria apagada.');
      recarregarCategorias();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // --- Tipos de atividade interna (Planejamento, Reuniões, ...): cada
  // instituição/área tem os próprios, cadastrados aqui pelo coordenador
  // daquela área (ou geral/master, em qualquer área) — ver
  // backend/tiposPontoInterno.js. Coordenador de área só pode escolher a
  // própria área ao criar; geral/master escolhem qualquer uma.
  const areasPermitidas =
    user?.perfil === 'coordenador' && user?.area_coordenacao
      ? AREAS.filter((a) => a.value === user.area_coordenacao)
      : AREAS;

  const { data: tipos = [] } = useQuery({
    queryKey: ['tipos-ponto-interno', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/tipos-ponto-interno`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId,
  });

  const [novaArea, setNovaArea] = useState(areasPermitidas[0]?.value || '');
  const [novoNome, setNovoNome] = useState('');
  const [novaCategoriaTipo, setNovaCategoriaTipo] = useState('');
  const [salvandoTipo, setSalvandoTipo] = useState(false);

  const recarregarTipos = () => {
    queryClient.invalidateQueries({ queryKey: ['tipos-ponto-interno'] });
    queryClient.invalidateQueries({ queryKey: ['pontos-agregado'] });
  };

  const criarTipo = async (e) => {
    e.preventDefault();
    if (!novoNome.trim()) return;
    setSalvandoTipo(true);
    try {
      const payload = { area: novaArea, nome: novoNome.trim() };
      if (novaCategoriaTipo) {
        payload.id_categoria = Number(novaCategoriaTipo);
      }
      const res = await fetch(`${API_URL}/api/tipos-ponto-interno`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao criar tipo');
      toast.success(json?.message || 'Tipo criado.');
      setNovoNome('');
      setNovaCategoriaTipo('');
      recarregarTipos();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoTipo(false);
    }
  };

  const alterarCategoriaDoTipo = async (tipo, idCat) => {
    try {
      const res = await fetch(`${API_URL}/api/tipos-ponto-interno/${tipo.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ id_categoria: idCat ? Number(idCat) : null }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao atualizar categoria do tipo');
      toast.success('Categoria atualizada.');
      recarregarTipos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const alternarAtivoTipo = async (tipo) => {
    try {
      const res = await fetch(`${API_URL}/api/tipos-ponto-interno/${tipo.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ ativo: tipo.ativo ? 0 : 1 }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao atualizar tipo');
      recarregarTipos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const apagarTipo = async (tipo) => {
    if (
      !(await confirm(`Apagar o tipo "${tipo.nome}"?`, {
        title: 'Apagar tipo',
        confirmText: 'Apagar',
      }))
    )
      return;
    try {
      const res = await fetch(`${API_URL}/api/tipos-ponto-interno/${tipo.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erro ao apagar tipo');
      toast.success(json?.message || 'Tipo apagado.');
      recarregarTipos();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const categoriasAtivas = useMemo(
    () => categorias.filter((c) => c.ativo),
    [categorias],
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <Card className="h-full">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <Tag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Categorias de Atividade
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Ex.: Aula, Planejamento, Estudo, Prática
                </p>
              </div>
            </div>

            {podeCriar && (
              <form onSubmit={criarCategoria} className="flex items-end gap-2 mb-4">
                <div className="flex-1">
                  <Input
                    label="Nova categoria"
                    size="sm"
                    value={novoNomeCategoria}
                    onChange={(e) => setNovoNomeCategoria(e.target.value)}
                    placeholder="Ex.: Estudo"
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  icon={Plus}
                  disabled={salvandoCategoria || !novoNomeCategoria.trim()}
                >
                  Criar Categoria
                </Button>
              </form>
            )}

            {categorias.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                Nenhuma categoria cadastrada.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {categorias.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <span
                        className={`text-sm font-medium truncate ${
                          c.ativo
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-400 dark:text-gray-600 line-through'
                        }`}
                      >
                        {c.nome}
                      </span>
                      {!c.ativo && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">(inativa)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {podeEditar && (
                        <button
                          onClick={() => alternarAtivoCategoria(c)}
                          title={c.ativo ? 'Desativar categoria' : 'Ativar categoria'}
                          className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {podeExcluir && (
                        <button
                          onClick={() => apagarCategoria(c)}
                          title="Apagar categoria"
                          className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="lg:col-span-7">
          <Card className="h-full">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                <Settings2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Tipos de Atividade Interna
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Vincule cada atividade a uma área e categoria para o relatório
                </p>
              </div>
            </div>

            {podeCriar && (
              <form onSubmit={criarTipo} className="flex items-end gap-2 mb-4 flex-wrap">
                <div className="w-36">
                  <Select
                    label="Área"
                    size="sm"
                    value={novaArea}
                    onChange={(e) => setNovaArea(e.target.value)}
                    options={areasPermitidas.map((a) => ({ value: a.value, label: a.label }))}
                  />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Input
                    label="Nome"
                    size="sm"
                    value={novoNome}
                    onChange={(e) => setNovoNome(e.target.value)}
                    placeholder="Ex.: Planejamento"
                  />
                </div>
                {categoriasAtivas.length > 0 && (
                  <div className="w-40">
                    <Select
                      label="Categoria"
                      size="sm"
                      value={novaCategoriaTipo}
                      onChange={(e) => setNovaCategoriaTipo(e.target.value)}
                      options={[
                        { value: '', label: 'Geral (padrão)' },
                        ...categoriasAtivas.map((c) => ({ value: String(c.id), label: c.nome })),
                      ]}
                    />
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  icon={Plus}
                  disabled={salvandoTipo || !novoNome.trim()}
                >
                  Adicionar
                </Button>
              </form>
            )}

            {tipos.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                Nenhum tipo cadastrado ainda.
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {tipos.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-xl bg-gray-50/60 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800"
                  >
                    <div className="min-w-0 flex items-center gap-2 flex-wrap">
                      <Badge tone="info">{AREA_LABEL[t.area] || t.area}</Badge>
                      <p
                        className={`text-sm font-medium truncate ${t.ativo ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-600'}`}
                      >
                        {t.nome}
                      </p>
                      {t.categoria_nome && <Badge tone="neutral">{t.categoria_nome}</Badge>}
                      {!t.ativo && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">(inativo)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {podeEditar && categorias.length > 0 && (
                        <select
                          aria-label={`Categoria de ${t.nome}`}
                          value={t.id_categoria ? String(t.id_categoria) : ''}
                          onChange={(e) => alterarCategoriaDoTipo(t, e.target.value)}
                          className="h-7 px-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300"
                        >
                          <option value="">Geral</option>
                          {categorias
                            .filter((c) => c.ativo || Number(c.id) === Number(t.id_categoria))
                            .map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nome}
                              </option>
                            ))}
                        </select>
                      )}
                      {podeEditar && (
                        <button
                          onClick={() => alternarAtivoTipo(t)}
                          title={t.ativo ? 'Desativar' : 'Ativar'}
                          className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {podeExcluir && (
                        <button
                          onClick={() => apagarTipo(t)}
                          title="Apagar"
                          className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Registro de Atividades dos Educadores
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="date"
              size="sm"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
            <span className="text-xs text-gray-400">até</span>
            <Input type="date" size="sm" value={fim} onChange={(e) => setFim(e.target.value)} />
            <div className="w-44">
              <Select
                size="sm"
                value={idProfessor}
                onChange={(e) => setIdProfessor(e.target.value)}
                options={[
                  { value: '', label: 'Todos os educadores' },
                  ...educadores.map((e) => ({ value: e.id, label: e.nome })),
                ]}
              />
            </div>
            {podeExportar && (
              <Button
                variant="secondary"
                icon={Download}
                onClick={gerarPDF}
                disabled={!idProfessor || pontos.length === 0 || gerandoPdf}
              >
                {gerandoPdf ? 'Gerando PDF...' : 'Gerar PDF'}
              </Button>
            )}
          </div>
        </div>
        {podeExportar && !idProfessor && (
          <p className="px-4 pt-2 text-xs text-gray-400 dark:text-gray-500">
            Selecione um educador acima pra gerar o relatório em PDF.
          </p>
        )}
        {pontos.length > 0 && (
          <div className="px-4 pt-3 pb-2">
            <ResumoCategorias registros={pontos} />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="px-4 py-2 font-medium">Data</th>
                <th className="px-4 py-2 font-medium">Educador</th>
                <th className="px-4 py-2 font-medium">Turma / Atividade</th>
                <th className="px-4 py-2 font-medium">Categoria</th>
                <th className="px-4 py-2 font-medium">Horário</th>
                <th className="px-4 py-2 font-medium">Entrada</th>
                <th className="px-4 py-2 font-medium">Saída</th>
                <th className="px-4 py-2 font-medium">Duração</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    Carregando...
                  </td>
                </tr>
              ) : pontos.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                    Nenhum registro nesse período.
                  </td>
                </tr>
              ) : (
                pontos.map((p) => (
                  <tr key={p.id} className="text-gray-700 dark:text-gray-300">
                    <td className="px-4 py-2">{formatarData(p.data)}</td>
                    <td className="px-4 py-2 truncate" translate="no">
                      {p.nome_professor}
                    </td>
                    <td className="px-4 py-2 truncate" translate="no">
                      {p.nome_turma}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone="neutral">
                        {p.categoria_nome || (p.id_atividade ? 'Aula' : 'Geral')}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                      {p.horario ? `${p.horario} (${p.turno})` : '—'}
                    </td>
                    <td className="px-4 py-2">{formatarHora(p.hora_entrada) || '—'}</td>
                    <td className="px-4 py-2">{formatarHora(p.hora_saida) || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                      {duracao(p.hora_entrada, p.hora_saida) || '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {podeEditar && (
                          <button
                            onClick={() => abrirEdicao(p)}
                            title="Corrigir"
                            className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {podeExcluir && (
                          <button
                            onClick={() => excluirPonto(p)}
                            title="Apagar"
                            className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

export default Pontos;
